import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Building2, Image as ImageIcon, ExternalLink } from "lucide-react";
import { Button } from "../ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  new: "Nouveau",
  contacted: "Contacté",
  quoted: "Devis envoyé",
  won: "Gagné",
  lost: "Perdu",
  archived: "Archivé",
};

const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-800 border-blue-300",
  contacted: "bg-amber-100 text-amber-800 border-amber-300",
  quoted: "bg-purple-100 text-purple-800 border-purple-300",
  won: "bg-emerald-100 text-emerald-800 border-emerald-300",
  lost: "bg-rose-100 text-rose-800 border-rose-300",
  archived: "bg-slate-100 text-slate-700 border-slate-300",
};

const CAT_LABELS = {
  automobile: "Automobile",
  moto: "Moto",
  nautique: "Nautique",
  habitat: "Habitat/mobilier",
  medical: "Médical",
  autre: "Autre",
};

const SOURCE_LABELS = {
  school: "Devis école",
  network: "Réseau STS (artisan)",
};

const SOURCE_COLORS = {
  school: "bg-[#1F2A4A] text-[#C9B896]",
  network: "bg-[#C9B896] text-[#1F2A4A]",
};

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const QuoteRequestsAdmin = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("all");

  const filtered = quotes.filter((q) =>
    sourceFilter === "all" ? true : (q.source || "school") === sourceFilter
  );

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/quotes`, { headers: authHeaders() });
      if (!r.ok) throw new Error("Chargement impossible");
      const data = await r.json();
      setQuotes(data);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      const r = await fetch(`${API}/api/admin/quotes/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ status }),
      });
      if (!r.ok) throw new Error("Impossible de mettre à jour");
      toast.success("Statut mis à jour");
      setQuotes((prev) => prev.map((q) => (q.id === id ? { ...q, status } : q)));
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div data-testid="quotes-admin">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-light text-[#1F2A4A]">Demandes de devis</h2>
          <p className="text-sm text-[#4A5568]">
            {filtered.length} affichée{filtered.length > 1 ? "s" : ""} sur {quotes.length} demande{quotes.length > 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2" data-testid="quotes-source-filter">
          {[
            { k: "all", label: `Toutes (${quotes.length})` },
            { k: "school", label: `Devis école (${quotes.filter((q) => (q.source || "school") === "school").length})` },
            { k: "network", label: `Réseau STS (${quotes.filter((q) => q.source === "network").length})` },
          ].map((o) => (
            <button
              key={o.k}
              type="button"
              onClick={() => { setSourceFilter(o.k); setSelected(null); }}
              className={`text-xs px-3.5 h-8 rounded-full border transition-colors ${sourceFilter === o.k ? "bg-[#1F2A4A] text-white border-[#1F2A4A]" : "bg-white text-[#1F2A4A] border-[#1F2A4A]/20 hover:border-[#C9B896]"}`}
              data-testid={`quotes-source-${o.k}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className="text-sm text-[#4A5568]">Chargement…</p>}

      {!loading && filtered.length === 0 && (
        <div className="bg-white rounded-xl border border-[#1F2A4A]/12 p-10 text-center">
          <p className="text-[#4A5568]">Aucune demande dans cette catégorie.</p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 space-y-3">
            {filtered.map((q) => (
              <button
                key={q.id}
                type="button"
                onClick={() => setSelected(q)}
                className={`w-full text-left rounded-xl border p-4 transition-colors ${selected?.id === q.id ? "border-[#1F2A4A] bg-[#FAF7F0]" : "border-[#1F2A4A]/12 bg-white hover:border-[#C9B896]"}`}
                data-testid={`quote-row-${q.id}`}
              >
                <div className="flex items-center justify-between gap-3 mb-1">
                  <p className="font-medium text-sm text-[#1F2A4A] truncate">{q.full_name}</p>
                  <span className={`shrink-0 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_COLORS[q.status] || STATUS_COLORS.new}`}>
                    {STATUS_LABELS[q.status] || q.status}
                  </span>
                </div>
                <p className="text-xs text-[#4A5568] truncate">{CAT_LABELS[q.category] || q.category}{q.category_precision ? ` — ${q.category_precision}` : ""}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`inline-block text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${SOURCE_COLORS[q.source || "school"]}`}>
                    {SOURCE_LABELS[q.source || "school"]}
                  </span>
                  <p className="text-[10px] text-[#4A5568]/70">{new Date(q.created_at).toLocaleString("fr-FR")}</p>
                </div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-3">
            {selected ? (
              <div className="bg-white rounded-xl border border-[#1F2A4A]/12 p-5 md:p-6" data-testid="quote-detail">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9B896] font-semibold mb-1">
                      {CAT_LABELS[selected.category] || selected.category}
                      {selected.category_precision && ` · ${selected.category_precision}`}
                    </p>
                    <h3 className="text-xl font-medium text-[#1F2A4A]">{selected.full_name}</h3>
                  </div>
                  <select
                    value={selected.status}
                    onChange={(e) => updateStatus(selected.id, e.target.value)}
                    className={`rounded-full px-3 py-1.5 text-xs border ${STATUS_COLORS[selected.status] || STATUS_COLORS.new}`}
                    data-testid="quote-status-select"
                  >
                    {Object.entries(STATUS_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5 text-sm">
                  <a href={`mailto:${selected.email}`} className="flex items-center gap-2 text-[#1F2A4A] hover:text-[#C9B896]">
                    <Mail className="w-4 h-4 text-[#C9B896]" />{selected.email}
                  </a>
                  {selected.phone && (
                    <a href={`tel:${selected.phone}`} className="flex items-center gap-2 text-[#1F2A4A] hover:text-[#C9B896]">
                      <Phone className="w-4 h-4 text-[#C9B896]" />{selected.phone}
                    </a>
                  )}
                  {selected.city && (
                    <p className="flex items-center gap-2 text-[#4A5568]">
                      <MapPin className="w-4 h-4 text-[#C9B896]" />{selected.city}
                    </p>
                  )}
                  {selected.company && (
                    <p className="flex items-center gap-2 text-[#4A5568]">
                      <Building2 className="w-4 h-4 text-[#C9B896]" />{selected.company}
                    </p>
                  )}
                </div>

                <div className="rounded-lg bg-[#FAF7F0] border border-[#1F2A4A]/8 p-4 mb-5">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9B896] font-semibold mb-1.5">Description</p>
                  <p className="text-sm text-[#1F2A4A] whitespace-pre-wrap leading-relaxed">{selected.project_description}</p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5 text-xs">
                  <Info label="Quantité" value={selected.quantity} />
                  <Info label="Date souhaitée" value={selected.delivery_date} />
                  <Info label="Flexibilité" value={selected.delivery_flexibility} />
                  <Info label="Budget" value={selected.budget_range} />
                </div>

                {selected.images?.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9B896] font-semibold mb-2 flex items-center gap-2">
                      <ImageIcon className="w-3.5 h-3.5" /> Inspirations ({selected.images.length})
                    </p>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {selected.images.map((img, i) => {
                        const url = `${API}/api/admin/quotes/${selected.id}/image/${encodeURIComponent(img.path.split('/').pop())}`;
                        return (
                          <a
                            key={i}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="relative block aspect-square rounded-lg overflow-hidden bg-slate-100 border border-slate-200 hover:border-[#C9B896]"
                            data-testid={`quote-image-${i}`}
                          >
                            {/* Admin fetch requires token; we fetch as blob then show */}
                            <QuoteImageThumb src={url} alt={img.filename} />
                            <ExternalLink className="absolute top-1 right-1 w-3.5 h-3.5 text-white drop-shadow" />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-dashed border-[#1F2A4A]/20 p-10 text-center text-sm text-[#4A5568]">
                Sélectionne une demande à gauche pour voir le détail.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Info = ({ label, value }) => (
  <div>
    <p className="text-[9px] uppercase tracking-[0.2em] text-[#4A5568] font-semibold">{label}</p>
    <p className="text-sm text-[#1F2A4A]">{value || "—"}</p>
  </div>
);

// Fetches the image with the auth header so admins can see it inline.
const QuoteImageThumb = ({ src, alt }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let revoke = null;
    (async () => {
      try {
        const r = await fetch(src, { headers: authHeaders() });
        if (!r.ok) return;
        const b = await r.blob();
        const u = URL.createObjectURL(b);
        revoke = u;
        setBlobUrl(u);
      } catch { /* silent */ }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [src]);
  if (!blobUrl) return <div className="w-full h-full bg-slate-100 animate-pulse" />;
  return <img src={blobUrl} alt={alt} className="w-full h-full object-cover" />;
};
